import React, { useEffect } from "react";

import { useDialog, useDialogs } from "@src/hooks/dialog";
import { useDispatch, useSelector } from "@src/store";
import {
  clearAiTestName,
  selectAiTestNameState,
} from "@src/store/slices/global";
import { toast } from "react-toastify";

import { usePatchExperience } from "@src/api/experiences/patch-experience";
import {
  mpAiTestNameSuggestionAccepted,
  mpAiTestNameSuggestionDismissed,
} from "@src/utils/mixpanel";

import { AcceptedName, PreSaveNameReviewDialog } from "./TestNameReviewDialog";

export const AiTestNameTableDialog = () => {
  const dispatch = useDispatch();
  const { closeDialog } = useDialogs();
  const dialog = useDialog("testNameReviewDialog");
  const experienceId = dialog.props?.experienceId;
  const experienceName = dialog.props?.experienceName ?? "";
  const aiTestNameState = useSelector(
    selectAiTestNameState(experienceId ?? "")
  );

  // If Redux state was cleared externally while the dialog is open (e.g. navigation),
  // close it — the state is already gone so no dispatch needed.
  useEffect(() => {
    if (
      dialog.isOpen &&
      experienceId &&
      aiTestNameState &&
      !aiTestNameState.flaggedNames.length
    ) {
      closeDialog("testNameReviewDialog");
    }
  }, [dialog.isOpen, experienceId, aiTestNameState, closeDialog]);

  const rename = usePatchExperience(experienceId, {
    invalidateLists: true,
    onSuccess: (data) => {
      if (experienceId) dispatch(clearAiTestName(experienceId));
      toast.success(`${data.experience.name} saved successfully`);
      closeDialog("testNameReviewDialog");
    },
    onError: () => {
      toast.error("Failed to save test name");
    },
  });

  if (!dialog.isOpen || !experienceId) return null;

  const testNameSuggestion = aiTestNameState?.flaggedNames.find(
    (f) => f.nameType === "test_name"
  );

  return (
    <PreSaveNameReviewDialog
      isOpen
      flaggedNames={aiTestNameState?.flaggedNames ?? []}
      onConfirm={(accepted: AcceptedName[]) => {
        if (rename.isPending) return;
        const testNameAccepted = accepted.find(
          (a) => a.nameType === "test_name"
        );
        if (testNameAccepted && testNameSuggestion) {
          mpAiTestNameSuggestionAccepted({
            experienceId: experienceId!,
            experienceName: experienceName,
            originalName: testNameSuggestion.originalName,
            suggestedName: testNameSuggestion.currentValue,
            acceptedName: testNameAccepted.value,
            wasEdited:
              testNameAccepted.value !== testNameSuggestion.currentValue,
          });
          rename.mutate({ id: experienceId, name: testNameAccepted.value });
        } else {
          if (testNameSuggestion) {
            mpAiTestNameSuggestionDismissed({
              experienceId: experienceId!,
              experienceName: experienceName,
              originalName: testNameSuggestion.originalName,
              suggestedName: testNameSuggestion.currentValue,
            });
          }
          dispatch(clearAiTestName(experienceId));
          closeDialog("testNameReviewDialog");
        }
      }}
      onDismiss={() => {
        if (testNameSuggestion) {
          mpAiTestNameSuggestionDismissed({
            experienceId: experienceId!,
            experienceName: experienceName,
            originalName: testNameSuggestion.originalName,
            suggestedName: testNameSuggestion.currentValue,
          });
        }
        dispatch(clearAiTestName(experienceId));
        closeDialog("testNameReviewDialog");
      }}
    />
  );
};
